import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, XCircle, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const SEV = {
  critical: { dot: 'bg-red-500', text: 'text-red-700', ring: 'border-red-200 bg-red-50', label: 'Critical' },
  warning: { dot: 'bg-amber-500', text: 'text-amber-700', ring: 'border-amber-200 bg-amber-50', label: 'Warning' },
  ok: { dot: 'bg-emerald-500', text: 'text-emerald-700', ring: 'border-emerald-200 bg-emerald-50', label: 'Healthy' },
};

const ConfigHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.request('/facilities/config-audit');
      setData(res);
      // Auto-expand any facility that isn't healthy
      const exp = {};
      (res.facilities || []).forEach((f) => { if (f.overall !== 'ok') exp[f.facility_id] = true; });
      setExpanded(exp);
    } catch (e) {
      toast({ title: 'Could not load config health', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;

  return (
    <Card data-testid="config-health-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center">
              {summary && summary.critical > 0
                ? <ShieldAlert className="mr-2 h-5 w-5 text-red-600" />
                : <ShieldCheck className="mr-2 h-5 w-5 text-emerald-600" />}
              Config Health
            </CardTitle>
            <CardDescription>Verifies each venue is fully wired (booking-ID prefix, Razorpay, hours, integrations). Check this right after creating a new venue.</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="config-health-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        {summary && (
          <div className="flex flex-wrap gap-2 pt-2" data-testid="config-health-summary">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
              <span className="h-2 w-2 rounded-full bg-red-500" /> {summary.critical} critical
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
              <span className="h-2 w-2 rounded-full bg-amber-500" /> {summary.warning} warning
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="h-2 w-2 rounded-full bg-emerald-500" /> {summary.ok} healthy
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading && <p className="text-sm text-gray-500">Loading config health…</p>}
        {!loading && data?.facilities?.length === 0 && <p className="text-sm text-gray-500">No facilities found.</p>}
        <div className="space-y-2">
          {!loading && (data?.facilities || []).map((f) => {
            const sev = SEV[f.overall] || SEV.ok;
            const isOpen = !!expanded[f.facility_id];
            return (
              <div key={f.facility_id} className={`border rounded-lg ${sev.ring}`} data-testid={`config-health-facility-${f.facility_id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded((p) => ({ ...p, [f.facility_id]: !p[f.facility_id] }))}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                  data-testid={`config-health-toggle-${f.facility_id}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${sev.dot}`} />
                    <span className="font-semibold truncate">{f.name}</span>
                    <span className={`text-xs font-medium ${sev.text} shrink-0`}>· {sev.label}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    {f.failing?.length > 0 && (
                      <span className="text-xs text-gray-500">{f.failing.length} issue{f.failing.length > 1 ? 's' : ''}</span>
                    )}
                    {isOpen ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 space-y-1.5 border-t border-white/60 pt-2">
                    {f.checks.map((c) => (
                      <div key={c.key} className="flex items-start gap-2 text-sm" data-testid={`config-check-${f.facility_id}-${c.key}`}>
                        {c.status === 'ok'
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                          : (c.severity === 'critical'
                            ? <XCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                            : <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />)}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-800">{c.label}</p>
                          <p className="text-xs text-gray-600">{c.detail}</p>
                          {c.status !== 'ok' && c.fix && (
                            <p className="text-xs text-gray-500 mt-0.5"><span className="font-semibold">Fix:</span> {c.fix}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ConfigHealthCard;
